/**
 * Authentication hook
 */
import { useState, useEffect } from 'react'
import {
  isAuthenticated,
  getCurrentUser,
  initiateLogin,
  logout,
  setAuthToken,
  setRefreshToken,
  meetsMinimum,
  type AuthUser,
  type UserRole,
} from '@/shared/lib/auth'

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Check for token in URL (from OAuth callback)
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const refreshToken = params.get('refresh_token')
    const errorParam = params.get('error')

    if (token) {
      setAuthToken(token)
      if (refreshToken) {
        setRefreshToken(refreshToken)
      }
      // Clean the OAuth callback params off the URL.
      window.history.replaceState({}, document.title, window.location.pathname)
    }

    if (errorParam === 'unauthorized') {
      setError('Access denied. Only authorized users can access this application.')
      setLoading(false)
      return
    }

    if (errorParam === 'auth_failed') {
      setError('Authentication failed. Please try again.')
      setLoading(false)
      return
    }

    if (!isAuthenticated()) {
      setLoading(false)
      return
    }

    // Fetch the real user (including role) from /auth/me. If the token is
    // invalid the helper clears it and returns null — we just drop into the
    // unauthenticated state and let the routing layer handle the redirect.
    getCurrentUser()
      .then(fetched => {
        if (!fetched) {
          setUser(null)
          setLoading(false)
          return
        }
        setUser({
          email: fetched.email,
          role: fetched.role,
        })
        setLoading(false)
      })
      .catch(() => {
        setUser(null)
        setLoading(false)
      })
  }, [])

  // Default to least privilege (`member`) until the user is loaded /
  // authenticated.
  const role: UserRole = user?.role ?? 'member'

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    role,
    hasRole: (minimum: UserRole) => meetsMinimum(user?.role, minimum),
    login: initiateLogin,
    logout,
  }
}
